// ./01 - PROJETOS DEV/meu-controle-de-estoque/src/components/modals/RegisterSaleModal.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { formatCurrency } from '../../utils/formatters';

const RegisterSaleModal = ({ isOpen, onClose, onSave, inventory, brands, customers, openSelectCustomerModal, initialData }) => {
    
    const getNextMonthDate = () => {
        const date = new Date();
        date.setMonth(date.getMonth() + 1);
        return date.toISOString().split('T')[0];
    };

    const initialSelectionState = { brand: '', productId: '', quantity: 1 };
    const initialPaymentState = {
        selectedCustomer: null,
        saleDate: new Date().toISOString().split('T')[0],
        useTodayForSaleDate: true,
        paymentType: 'À Vista',
        paymentMethod: 'Pix',
        installmentCount: 2,
        firstDueDate: new Date().toISOString().split('T')[0],
        useTodayForPaymentDate: true,
        termDate: getNextMonthDate()
    };

    const [step, setStep] = useState(1);
    const [isSaving, setIsSaving] = useState(false);
    const [cartItems, setCartItems] = useState([]);
    const [selectionState, setSelectionState] = useState(initialSelectionState);
    const [paymentState, setPaymentState] = useState(initialPaymentState);
    const [editableSalePrice, setEditableSalePrice] = useState('');
    
    const [isGiftItem, setIsGiftItem] = useState(false);
    const [saleSummary, setSaleSummary] = useState(null);

    const resetModalState = useCallback(() => {
        setCartItems([]);
        setSelectionState(initialSelectionState);
        setPaymentState(initialPaymentState);
        setStep(1);
        setIsSaving(false);
        setEditableSalePrice('');
        setIsGiftItem(false);
        setSaleSummary(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isOpen) {
            if (initialData?.payload) {
                setCartItems(initialData.payload.cartItems || []);
                setPaymentState({
                    ...initialData.payload.paymentState,
                    selectedCustomer: initialData.selectedCustomer
                });
                setStep(2);
            } else if (initialData?.cartItems && initialData.cartItems.length > 0) {
                const mappedCart = initialData.cartItems.map(item => ({
                    id: item.id,
                    name: item.name,
                    quantity: item.sellQty, 
                    salePrice: item.salePrice, // Aqui entra o preço padrão do PDV
                    cost: item.cost,
                    stockAvailable: item.balance
                }));
                setCartItems(mappedCart);
                setStep(1); 
            }
        } else {
            const timer = setTimeout(resetModalState, 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen, initialData, resetModalState]);

    useEffect(() => {
        if (paymentState.useTodayForSaleDate) {
            setPaymentState(prev => ({ ...prev, saleDate: new Date().toISOString().split('T')[0] }));
        }
    }, [paymentState.useTodayForSaleDate]);

    useEffect(() => {
        if (paymentState.useTodayForPaymentDate) {
            setPaymentState(prev => ({ ...prev, firstDueDate: new Date().toISOString().split('T')[0] }));
        }
    }, [paymentState.useTodayForPaymentDate]);

    const productsByBrand = useMemo(() => {
        if (!selectionState.brand) return [];
        return inventory
            .filter(p => p.brand === selectionState.brand)
            .filter(p => {
                const inCartQty = (cartItems || []).find(item => item.id === p.id)?.quantity || 0;
                const availableStock = (p.initialQty - p.soldQty) - inCartQty;
                return availableStock > 0;
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [inventory, selectionState.brand, cartItems]);

    const selectedProductDetails = useMemo(() => {
        if (!selectionState.productId) return null;
        return inventory.find(p => p.id === selectionState.productId);
    }, [inventory, selectionState.productId]);

    useEffect(() => {
        if (selectedProductDetails) {
            setEditableSalePrice(selectedProductDetails.salePrice.toFixed(2));
            setIsGiftItem(false); 
        } else {
            setEditableSalePrice('');
            setIsGiftItem(false);
        }
    }, [selectedProductDetails]);

    const stockAvailableForSelection = useMemo(() => {
        if (!selectedProductDetails) return 0;
        const inCartQty = (cartItems || []).find(item => item.id === selectedProductDetails.id)?.quantity || 0;
        return (selectedProductDetails.initialQty - selectedProductDetails.soldQty) - inCartQty;
    }, [selectedProductDetails, cartItems]);

    // Calcula o total convertendo com segurança o valor (para evitar erros ao digitar vírgulas/pontos)
    const cartTotal = useMemo(() => {
        return (cartItems || []).reduce((total, item) => total + ((parseFloat(item.salePrice) || 0) * item.quantity), 0);
    }, [cartItems]);

    const handleSelectionChange = (e) => {
        const { name, value } = e.target;
        const newQty = name === 'quantity' ? Number(value) : value;
        setSelectionState(prev => {
            const newState = { ...prev, [name]: newQty };
            if (name === 'brand') {
                newState.productId = '';
            }
            return newState;
        });
    };

    const handleAddToCart = () => {
        if (!selectedProductDetails) return;
        
        const parsedPrice = parseFloat(editableSalePrice);
        const finalSalePrice = !isNaN(parsedPrice) ? parsedPrice : selectedProductDetails.salePrice;

        setCartItems(prevItems => {
            const existingItem = (prevItems || []).find(item => item.id === selectedProductDetails.id);
            if (existingItem) {
                return (prevItems || []).map(item => 
                    item.id === selectedProductDetails.id 
                    ? { ...item, quantity: item.quantity + selectionState.quantity, salePrice: finalSalePrice } 
                    : item
                );
            } else {
                return [...(prevItems || []), { 
                    id: selectedProductDetails.id, 
                    name: selectedProductDetails.name,
                    quantity: selectionState.quantity,
                    salePrice: finalSalePrice,
                    cost: selectedProductDetails.cost,
                    stockAvailable: selectedProductDetails.initialQty - selectedProductDetails.soldQty
                }];
            }
        });
        setSelectionState(initialSelectionState);
    };

    const handleCartQuantityChange = (productId, newQuantity) => {
        const numQuantity = Number(newQuantity);
        if (isNaN(numQuantity)) return;

        setCartItems(prevItems => (prevItems || []).map(item => 
            item.id === productId ? { ...item, quantity: numQuantity } : item
        ).filter(item => item.quantity > 0));
    };

    // NOVO: Função para alterar o preço diretamente dentro do carrinho
    const handleCartPriceChange = (productId, newPrice) => {
        setCartItems(prevItems => (prevItems || []).map(item => 
            item.id === productId ? { ...item, salePrice: newPrice } : item
        ));
    };

    const handlePaymentStateChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;
        setPaymentState(prev => ({...prev, [name]: val}));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSaving || !cartItems || cartItems.length === 0) return;
        setIsSaving(true);

        const isGiftSale = cartTotal === 0;
        const finalPaymentType = isGiftSale ? 'À Vista' : paymentState.paymentType;
        const finalPaymentMethod = isGiftSale ? 'Brinde' : paymentState.paymentMethod;

        const saleData = {
            items: cartItems.map(item => ({
                productId: item.id,
                productName: item.name,
                quantity: item.quantity,
                unitPrice: parseFloat(item.salePrice) || 0, // Converte a string do input do carrinho para numérico
                cost: item.cost
            })),
            itemCount: cartItems.length,
            totalQuantity: (cartItems || []).reduce((sum, item) => sum + item.quantity, 0),
            totalAmount: cartTotal,
            customerId: paymentState.selectedCustomer?.id || null,
            customerName: paymentState.selectedCustomer?.name || null,
            saleDate: new Date(`${paymentState.saleDate}T12:00:00`), 
            paymentType: finalPaymentType,
            paymentMethod: finalPaymentMethod,
            status: 'Pendente',
            installments: []
        };

        if (finalPaymentType === 'À Vista') {
             saleData.status = 'Concluída';
             const paymentDate = new Date(`${paymentState.firstDueDate}T12:00:00`); 
             saleData.installments.push({ installmentNumber: 1, amount: cartTotal, dueDate: paymentDate, status: 'Paga', paidAmount: cartTotal });
        } else if (finalPaymentType === 'Por Prazo') {
             const dueDate = new Date(`${paymentState.termDate}T12:00:00`);
             saleData.installments.push({ installmentNumber: 1, amount: cartTotal, dueDate: dueDate, status: 'Pendente', paidAmount: 0 });
        } else if (finalPaymentType === 'Parcelado') {
            const numInstallments = Number(paymentState.installmentCount);
            const installmentAmount = parseFloat((cartTotal / numInstallments).toFixed(2));
            const firstDueDate = new Date(`${paymentState.firstDueDate}T12:00:00`);

            const generatedInstallments = Array.from({ length: numInstallments }, (_, i) => {
                const dueDate = new Date(firstDueDate);
                dueDate.setMonth(dueDate.getMonth() + i);
                return { installmentNumber: i + 1, amount: installmentAmount, dueDate: dueDate, status: 'Pendente', paidAmount: 0 };
            });

            const totalCalculated = generatedInstallments.reduce((acc, inst) => acc + inst.amount, 0);
            const diff = cartTotal - totalCalculated;
            if (generatedInstallments.length > 0) {
              generatedInstallments[generatedInstallments.length - 1].amount += parseFloat(diff.toFixed(2));
            }
            saleData.installments = generatedInstallments;
        }

        try {
            await onSave(saleData);

            const totalCost = cartItems.reduce((acc, item) => acc + ((parseFloat(item.cost) || 0) * item.quantity), 0);
            const profit = cartTotal - totalCost;

            setSaleSummary({
                total: cartTotal,
                profit: profit,
                isGift: isGiftSale
            });

            const successCallback = initialData?.onSuccess || initialData?.payload?.onSuccess;
            if (successCallback) {
                successCallback();
            }

            setStep(3);

        } catch (error) {
            console.error("Falha ao registrar a venda:", error);
            alert(`Erro ao salvar: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };
    
    if (!isOpen) return null;

    const renderStepContent = () => {
        switch (step) {
            case 1:
                return (
                     <div className="space-y-6">
                        <div className="space-y-4 p-4 border rounded-lg">
                            <h3 className="font-semibold text-lg text-gray-700">Selecionar Produto</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="form-label">Marca</label>
                                    <select name="brand" value={selectionState.brand} onChange={handleSelectionChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                                        <option value="">Selecione a marca...</option>
                                        {brands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="form-label">Produto</label>
                                    <select name="productId" value={selectionState.productId} onChange={handleSelectionChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" disabled={!selectionState.brand}>
                                        <option value="">Selecione o produto...</option>
                                        {productsByBrand.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                    </select>
                                </div>
                            </div>
                            {selectedProductDetails && (
                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 items-end bg-gray-50 p-3 rounded-lg">
                                    <div className="sm:col-span-1">
                                        <label className="form-label text-sm">Custo</label>
                                        <p className="font-semibold text-gray-500">{formatCurrency(selectedProductDetails.cost)}</p>
                                    </div>
                                    <div className="sm:col-span-1">
                                        <label htmlFor="editableSalePrice" className="form-label text-sm">Preço Venda</label>
                                        <input id="editableSalePrice" type="number" step="0.01" value={editableSalePrice} onChange={e => {
                                            setEditableSalePrice(e.target.value);
                                            if(Number(e.target.value) > 0) setIsGiftItem(false);
                                        }} className="w-full text-center px-2 py-1 border-gray-300 rounded-lg"/>
                                    </div>
                                    
                                    <div className="sm:col-span-1 flex items-center h-full pb-1">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" checked={isGiftItem} onChange={(e) => {
                                                setIsGiftItem(e.target.checked);
                                                setEditableSalePrice(e.target.checked ? '0' : selectedProductDetails.salePrice.toFixed(2));
                                            }} className="h-4 w-4 rounded border-gray-300 text-[#D7BFBD] focus:ring-[#D7BFBD]" />
                                            <span className="text-sm font-semibold text-gray-600">Brinde?</span>
                                        </label>
                                    </div>

                                    <div className="sm:col-span-1">
                                        <label className="form-label text-sm">Estoque</label>
                                        <p className="font-semibold">{stockAvailableForSelection}</p>
                                    </div>
                                    <div className="sm:col-span-1">
                                        <label htmlFor="quantity" className="form-label text-sm">Qtd.</label>
                                        <input id="quantity" name="quantity" type="number" value={selectionState.quantity} onChange={handleSelectionChange} min="1" max={stockAvailableForSelection} className="w-full text-center px-2 py-1 border-gray-300 rounded-lg"/>
                                    </div>
                                </div>
                            )}
                            <button onClick={handleAddToCart} className="custom-button w-full font-semibold py-2 px-4 rounded-lg" disabled={!selectedProductDetails || selectionState.quantity < 1 || selectionState.quantity > stockAvailableForSelection}>
                                Adicionar ao Carrinho
                            </button>
                        </div>
                        
                        {cartItems && cartItems.length > 0 && (
                            <div className="space-y-4 pt-4 border-t">
                                <h3 className="text-lg font-bold text-gray-800">Carrinho de Compras</h3>
                                <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                                    {cartItems.map(item => {
                                        const stockInDB = inventory.find(p => p.id === item.id)?.initialQty - inventory.find(p => p.id === item.id)?.soldQty || 0;
                                        const itemPrice = parseFloat(item.salePrice) || 0;
                                        return (
                                            <div key={item.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-white border border-gray-200 p-3 rounded-lg gap-4 shadow-sm">
                                                <div className="flex-1">
                                                    <p className="font-bold text-gray-800">{item.name}</p>
                                                    <p className="text-xs text-gray-500 font-medium">Subtotal: {formatCurrency(itemPrice * item.quantity)}</p>
                                                </div>
                                                <div className="flex items-center gap-3 self-end sm:self-center">
                                                    {/* NOVO: Input de preço diretamente na lista do carrinho */}
                                                    <div className="flex flex-col items-center">
                                                        <label className="text-[10px] text-gray-500 font-semibold uppercase mb-1">Preço (R$)</label>
                                                        <input 
                                                            type="number" 
                                                            step="0.01"
                                                            value={item.salePrice} 
                                                            onChange={e => handleCartPriceChange(item.id, e.target.value)} 
                                                            className="w-24 text-center px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-[#D7BFBD] focus:border-[#D7BFBD]"
                                                        />
                                                    </div>
                                                    <div className="flex flex-col items-center">
                                                        <label className="text-[10px] text-gray-500 font-semibold uppercase mb-1">Qtd</label>
                                                        <input 
                                                            type="number" 
                                                            value={item.quantity} 
                                                            onChange={e => handleCartQuantityChange(item.id, e.target.value)} 
                                                            min="1" 
                                                            max={stockInDB} 
                                                            className="w-16 text-center px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-[#D7BFBD] focus:border-[#D7BFBD]"
                                                        />
                                                    </div>
                                                    <button onClick={() => handleCartQuantityChange(item.id, 0)} className="text-red-500 hover:text-red-700 mt-5 text-xl" title="Remover item">
                                                        🗑️
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                                <div className="text-right font-bold text-2xl pt-4 text-gray-800 border-t">
                                    Total: {formatCurrency(cartTotal)}
                                </div>
                            </div>
                        )}
                    </div>
                );
            case 2:
                return (
                    <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                            <div className="flex-grow">
                                <label htmlFor="saleDate" className="form-label">Data da Venda</label>
                                <input id="saleDate" name="saleDate" type="date" value={paymentState.saleDate} onChange={handlePaymentStateChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" readOnly={paymentState.useTodayForSaleDate} />
                            </div>
                            <div className="flex items-center pb-2">
                                <input id="useTodayForSaleDate" name="useTodayForSaleDate" type="checkbox" checked={paymentState.useTodayForSaleDate} onChange={handlePaymentStateChange} className="h-4 w-4 rounded border-gray-300" />
                                <label htmlFor="useTodayForSaleDate" className="ml-2 block text-sm">Hoje</label>
                            </div>
                        </div>
                        <div>
                            <label className="form-label">Cliente</label>
                            <div className="flex flex-col sm:flex-row items-center gap-4 p-3 bg-gray-50 rounded-lg border">
                                <div className="flex-grow text-center sm:text-left">
                                    {paymentState.selectedCustomer ? <p className="font-semibold">{paymentState.selectedCustomer.name}</p> : <p className="text-gray-500">Nenhum cliente selecionado</p>}
                                </div>
                                <button type="button" onClick={() => openSelectCustomerModal({ 
                                    cartItems, 
                                    paymentState, 
                                    onSuccess: initialData?.onSuccess || initialData?.payload?.onSuccess 
                                })} className="custom-button-outline text-sm w-full sm:w-auto">
                                    {paymentState.selectedCustomer ? 'Trocar Cliente' : 'Selecionar Cliente'}
                                </button>
                            </div>
                        </div>
                        
                        <div className="space-y-4 pt-4 border-t">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                                <h3 className="text-lg font-semibold">Detalhes do Pagamento</h3>
                                <div className="text-xl font-bold self-end sm:self-center">Total: {formatCurrency(cartTotal)}</div>
                            </div>
                            
                            {cartTotal === 0 ? (
                                <div className="bg-purple-50 p-4 rounded-xl border border-purple-200 mt-4 flex items-center gap-4">
                                    <div className="text-4xl">🎁</div>
                                    <div>
                                        <h4 className="font-bold text-purple-800">Venda Bonificada / Brinde</h4>
                                        <p className="text-sm text-purple-600">Como o valor total é R$ 0,00, não serão geradas cobranças financeiras. Apenas a baixa no estoque será realizada.</p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="form-label">Tipo de Pagamento</label>
                                            <select name="paymentType" value={paymentState.paymentType} onChange={handlePaymentStateChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                                                <option value="À Vista">À Vista</option>
                                                <option value="Por Prazo">Por Prazo</option>
                                                <option value="Parcelado">Parcelado</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="form-label">Método de Pagamento</label>
                                            <select name="paymentMethod" value={paymentState.paymentMethod} onChange={handlePaymentStateChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                                                <option value="Pix">Pix</option>
                                                <option value="Cartão de Crédito">Cartão de Crédito</option>
                                                <option value="Dinheiro">Dinheiro</option>
                                                <option value="Boleto">Boleto</option>
                                            </select>
                                        </div>
                                    </div>
                                
                                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-4">
                                        {paymentState.paymentType === 'À Vista' && (
                                            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                                                <div className="flex-grow">
                                                    <label className="form-label">Data do Pagamento</label>
                                                    <input name="firstDueDate" type="date" value={paymentState.firstDueDate} onChange={handlePaymentStateChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" readOnly={paymentState.useTodayForPaymentDate} />
                                                </div>
                                                <div className="flex items-center pb-2">
                                                    <input name="useTodayForPaymentDate" type="checkbox" checked={paymentState.useTodayForPaymentDate} onChange={handlePaymentStateChange} className="h-4 w-4 rounded border-gray-300" />
                                                    <label htmlFor="useTodayForPaymentDate" className="ml-2 block text-sm">Hoje</label>
                                                </div>
                                            </div>
                                        )}
                                        {paymentState.paymentType === 'Por Prazo' && (
                                            <div>
                                                <label className="form-label">Data de Vencimento</label>
                                                <input name="termDate" type="date" value={paymentState.termDate} onChange={handlePaymentStateChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                                            </div>
                                        )}
                                        {paymentState.paymentType === 'Parcelado' && (
                                            <>
                                                <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                                                    <div className="flex-grow">
                                                        <label className="form-label">Data da 1ª Parcela</label>
                                                        <input name="firstDueDate" type="date" value={paymentState.firstDueDate} onChange={handlePaymentStateChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" readOnly={paymentState.useTodayForPaymentDate} />
                                                    </div>
                                                    <div className="flex items-center pb-2">
                                                        <input id="useTodayForPaymentDate" name="useTodayForPaymentDate" type="checkbox" checked={paymentState.useTodayForPaymentDate} onChange={handlePaymentStateChange} className="h-4 w-4 rounded border-gray-300" />
                                                        <label htmlFor="useTodayForPaymentDate" className="ml-2 block text-sm">Hoje</label>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="form-label">Quantidade de Parcelas</label>
                                                    <select name="installmentCount" value={paymentState.installmentCount} onChange={handlePaymentStateChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg">
                                                        {Array.from({ length: 11 }, (_, i) => i + 2).map(n => (
                                                            <option key={n} value={n}>{n}x</option>
                                                        ))}
                                                    </select>
                                                    {paymentState.installmentCount > 1 && cartTotal > 0 && (
                                                        <p className="text-sm text-gray-600 mt-2">
                                                            Aproximadamente {formatCurrency(cartTotal / paymentState.installmentCount)} por parcela.
                                                        </p>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="flex flex-col items-center justify-center py-6 animate-fade-in-up space-y-4">
                        {saleSummary?.isGift ? (
                            <div className="w-24 h-24 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-5xl shadow-inner">
                                🎁
                            </div>
                        ) : (
                            <div className="w-24 h-24 bg-green-100 text-green-500 rounded-full flex items-center justify-center text-5xl shadow-inner">
                                ✓
                            </div>
                        )}
                        
                        <h3 className="text-2xl font-extrabold text-gray-800 text-center">
                            {saleSummary?.isGift ? 'Brinde Registrado com Sucesso!' : 'Venda Concluída com Sucesso!'}
                        </h3>
                        <p className="text-gray-500 text-center max-w-sm">
                            O registro foi salvo e o estoque já foi atualizado na sua base de dados.
                        </p>

                        <div className="w-full max-w-sm bg-gray-50 border border-gray-200 rounded-xl p-5 mt-4 space-y-3 shadow-sm">
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600 font-medium">Valor Total da Venda:</span>
                                <span className="text-xl font-bold text-gray-800">{formatCurrency(saleSummary?.total || 0)}</span>
                            </div>
                            <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                                <span className="text-gray-600 font-medium">Lucro Bruto (Operação):</span>
                                <span className={`text-lg font-bold ${saleSummary?.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {formatCurrency(saleSummary?.profit || 0)}
                                </span>
                            </div>
                        </div>
                    </div>
                );
            default: return null;
        }
    };
    
    return (
        <div className="modal-overlay z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-3xl m-4 sm:m-8 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-start">
                    <h2 className="text-2xl font-bold text-gray-800">
                        {step === 3 ? 'Resumo da Operação' : 'Registrar Venda'}
                    </h2>
                    {step < 3 && <span className="text-sm font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">Etapa {step} de 2</span>}
                </div>
                <div className="mt-6">
                    {renderStepContent()}
                </div>
                
                <div className="flex flex-col-reverse sm:flex-row justify-between gap-4 pt-6 border-t border-gray-200 mt-6">
                    {step === 3 ? (
                        <div className="w-full flex justify-center">
                            <button type="button" onClick={onClose} className="custom-button py-3 px-8 font-bold rounded-xl w-full sm:w-auto shadow-md">
                                Fechar e Voltar
                            </button>
                        </div>
                    ) : (
                        <>
                            {step === 1 ? (
                                <button type="button" onClick={onClose} className="py-2 px-6 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 w-full sm:w-auto">Cancelar</button>
                            ) : (
                                <button type="button" onClick={() => setStep(1)} className="py-2 px-6 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 w-full sm:w-auto">Voltar</button>
                            )}
                            
                            {step === 1 ? (
                                <button type="button" onClick={() => setStep(2)} className="custom-button py-2 px-6 font-semibold rounded-lg w-full sm:w-auto" disabled={!cartItems || cartItems.length === 0}>Avançar</button>
                            ) : (
                                <button type="submit" onClick={handleSubmit} className="custom-button py-2 px-6 font-semibold rounded-lg w-full sm:w-auto" disabled={isSaving || !paymentState.selectedCustomer}>
                                    {isSaving ? 'Salvando...' : 'Registrar Venda'}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RegisterSaleModal;